import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useSignIn, useAuth } from '@clerk/clerk-react'
import { getSafeRedirectPath } from '@/lib/redirect'
import { ROUTES } from '@/routes/routes'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { AuthLayout } from '@/components/auth/AuthLayout'
import { GoogleIcon } from '@/components/auth/GoogleIcon'
import { markPendingSignInMethod } from '@/lib/pendingSignInMethod'
import { clerkErrorMessage } from '@/lib/clerkError'
import { completeAuthAndRedirect } from '@/lib/postAuthRedirect'

export default function SignInPage() {
  const { isLoaded, signIn, setActive } = useSignIn()
  const { isSignedIn } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const redirectTo = getSafeRedirectPath(searchParams.get('redirectTo'))

  const [step, setStep] = useState<'password' | 'totp'>('password')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (isSignedIn) {
      navigate(redirectTo, { replace: true })
    }
  }, [isSignedIn, navigate, redirectTo])

  async function onSubmitPassword(e: React.FormEvent) {
    e.preventDefault()
    if (!isLoaded || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      const result = await signIn.create({ strategy: 'password', identifier: email, password })
      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId })
        await completeAuthAndRedirect(navigate, redirectTo, 'email_password')
      } else if (result.status === 'needs_second_factor') {
        setStep('totp')
      } else {
        setError('Additional verification is required. Please try again.')
      }
    } catch (err) {
      setError(clerkErrorMessage(err, 'Unable to sign in. Please check your email and password.'))
    } finally {
      setSubmitting(false)
    }
  }

  async function onGoogleSignIn() {
    if (!isLoaded || submitting) return
    setError(null)
    try {
      markPendingSignInMethod('google')
      await signIn.authenticateWithRedirect({
        strategy: 'oauth_google',
        redirectUrl: ROUTES.AUTH.SSO_CALLBACK,
        redirectUrlComplete: redirectTo,
      })
    } catch (err) {
      setError(clerkErrorMessage(err, 'Unable to continue with Google. Please try again.'))
    }
  }

  async function onSubmitTotp(e: React.FormEvent) {
    e.preventDefault()
    if (!isLoaded || code.length !== 6 || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      const result = await signIn.attemptSecondFactor({ strategy: 'totp', code })
      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId })
        await completeAuthAndRedirect(navigate, redirectTo, 'email_password')
      } else {
        setError('That code didn’t complete sign-in. Please try again.')
        setCode('')
      }
    } catch (err) {
      setError(clerkErrorMessage(err, 'Invalid code. Please try again.'))
      setCode('')
    } finally {
      setSubmitting(false)
    }
  }

  if (step === 'totp') {
    return (
      <AuthLayout
        title="Two-factor authentication"
        subtitle="Enter the 6-digit code from your authenticator app."
      >
        <form onSubmit={onSubmitTotp} className="space-y-4">
          <Input
            type="text"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            autoComplete="one-time-code"
            autoFocus
            placeholder="000000"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className="text-center font-mono text-2xl tracking-widest text-[var(--fl-heading)]"
            aria-label="6-digit verification code"
          />

          {error && (
            <div className="rounded-md bg-[var(--fl-error-bg)] px-4 py-3 text-sm text-[var(--fl-error)]">
              {error}
            </div>
          )}

          <Button type="submit" disabled={code.length !== 6 || submitting} className="w-full">
            {submitting ? 'Verifying…' : 'Verify'}
          </Button>
        </form>

        <p className="text-xs text-[var(--fl-text-3)] text-center">
          <Button
            variant="link"
            type="button"
            onClick={() => {
              setStep('password')
              setCode('')
              setError(null)
            }}
            className="h-auto p-0 text-xs font-normal"
          >
            Back to sign in
          </Button>
        </p>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout title="Sign in" subtitle="Welcome back to LogMySpends.">
      <Button
        type="button"
        variant="outline"
        onClick={onGoogleSignIn}
        disabled={!isLoaded || submitting}
        className="w-full"
      >
        <GoogleIcon className="h-4 w-4" />
        Continue with Google
      </Button>

      <div className="flex items-center gap-3">
        <div className="h-px flex-1 bg-[var(--fl-border)]" />
        <span className="text-xs text-[var(--fl-text-3)]">or</span>
        <div className="h-px flex-1 bg-[var(--fl-border)]" />
      </div>

      <form onSubmit={onSubmitPassword} className="space-y-4">
        <div className="space-y-1">
          <label htmlFor="email" className="text-sm font-medium text-[var(--fl-heading)]">
            Email
          </label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
            autoComplete="email"
            required
          />
        </div>

        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <label htmlFor="password" className="text-sm font-medium text-[var(--fl-heading)]">
              Password
            </label>
            <Link
              to={ROUTES.AUTH.FORGOT_PASSWORD}
              className="text-xs text-[var(--fl-primary)] hover:underline"
            >
              Forgot password?
            </Link>
          </div>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </div>

        {error && (
          <div className="rounded-md bg-[var(--fl-error-bg)] px-4 py-3 text-sm text-[var(--fl-error)]">
            {error}
          </div>
        )}

        <Button type="submit" disabled={!isLoaded || submitting} className="w-full">
          {submitting ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>

      <p className="text-xs text-[var(--fl-text-3)] text-center">
        New to LogMySpends?{' '}
        <Link
          to={`${ROUTES.AUTH.SIGN_UP}?redirectTo=${encodeURIComponent(redirectTo)}`}
          className="text-[var(--fl-primary)] hover:underline"
        >
          Create an account
        </Link>
      </p>
    </AuthLayout>
  )
}
